import {
  CreateDoctorDto,
  DoctorDto,
  DoctorListDto,
  DoctorType,
  QueryFactory,
} from '@lmt/shared-types';
import { useMemo } from 'react';
import * as yup from 'yup';
import i18next from 'i18next';
import { DoctorData } from './types';
import {
  emailRegexp,
  phoneRegexp,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';

export const doctorDataYupSchema: yup.Schema<Partial<DoctorData>> = yup.object({
  doctorType: yup
    .mixed()
    .required(() => i18next.t('translation:errors.required')) as any,
  name: yup
    .string()
    .trim()
    .max(100, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 100,
      }),
    ),
  importId: yup.string() as any,
  surname: yup
    .string()
    .trim()
    .max(100, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 100,
      }),
    )
    .required(() => i18next.t('translation:errors.required')),
  titleId: yup.number().nullable(),
  titleOfLetter: yup
    .string()
    .trim()
    .max(100, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 100,
      }),
    ),
  specialtyId: yup.number().nullable(),
  mobilePhone: yup
    .string()
    .trim()
    .max(50, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 50,
      }),
    ),
  // .matches(phoneRegexp, {
  //   excludeEmptyString: true,
  //   message: () => i18next.t('errors.invalid'),
  // })
  workPhone: yup
    .string()
    .trim()
    .max(50, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 50,
      }),
    ),
  // .matches(phoneRegexp, {
  //   excludeEmptyString: true,
  //   message: () => i18next.t('errors.invalid'),
  // })
  email: yup
    .string()
    .trim()
    .max(254, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 254,
      }),
    )
    .matches(emailRegexp, {
      excludeEmptyString: true,
      message: () => i18next.t('translation:errors.invalid'),
    }),
  fax: yup
    .string()
    .trim()
    .max(50, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 50,
      }),
    )
    .matches(phoneRegexp, {
      excludeEmptyString: true,
      message: () => i18next.t('translation:errors.invalid'),
    }),
  address: yup
    .string()
    .trim()
    .max(100, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 100,
      }),
    ),
  clinicId: yup.number().nullable(),
  location: yup
    .string()
    .trim()
    .max(100, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 100,
      }),
    ),
  postcode: yup
    .string()
    .trim()
    .max(20, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 20,
      }),
    ),
  cisId: yup
    .string()
    .trim()
    .max(20, () =>
      i18next.t('translation:errors.max_length', {
        maxLength: 20,
      }),
    ),
  active: yup
    .boolean()
    .required(() => i18next.t('translation:errors.required')),
  isUsedAsResource: yup.boolean(),
  resourceName: yup.string().when('isUsedAsResource', {
    is: true,
    then: (schema) =>
      schema.required(() => i18next.t('translation:errors.required')),
    otherwise: (schema) => schema.nullable(),
  }),
});

export function useActiveReferringDoctors(
  searchQuery: string | null,
  currentDoctorId?: unknown,
) {
  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    doctorType: DoctorType.External,
    all: true,
    isOnlyActive: true,
    searchQuery: searchQuery,
    includeDeleted: true,
  });
  return useMemo(() => {
    return (doctorsQuery.data?.data ?? []).filter(
      /* currently selected value is shown even if deleted */
      (x) => !x.isDeleted || x.id === currentDoctorId,
    );
  }, [doctorsQuery.data, currentDoctorId]);
}

export function useActiveAttendingDoctors(
  searchQuery: string | null,
  currentDoctorId?: unknown,
) {
  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    doctorType: DoctorType.Clinic,
    all: true,
    isOnlyActive: true,
    searchQuery: searchQuery,
    includeDeleted: true,
  });
  return useMemo(() => {
    return (doctorsQuery.data?.data ?? []).filter(
      /* currently selected value is shown even if deleted */
      (x) => !x.isDeleted || x.id === currentDoctorId,
    );
  }, [doctorsQuery.data, currentDoctorId]);
}

export function getDoctorFullName(doctor: DoctorDto | DoctorListDto) {
  if (!doctor.name) {
    return doctor.surname;
  }
  return doctor.surname + ' ' + doctor.name;
}
